/**
 * NearestStationSelector - Smart station selection component
 *
 * Features:
 * - Two action buttons: "Auto Search" and "Manual Search"
 * - Auto Search: finds nearest stations based on validated coordinates
 * - Manual Search: opens search dropdown with distance calculation
 * - Groups stations by name (shows all line logos for multi-line stations)
 * - Displays stations as selectable chips with distance
 * - Multi-select support (up to 5 stations)
 */

import { useCallback, useMemo, useState } from "react";
import { X, Sparkles, Train, Search, RefreshCw } from "lucide-react";
import { observer } from "mobx-react-lite";

import { useTranslation } from "@/shared/hooks";
import { useStore } from "@/stores/useStore";
import { RailwayLineLogo } from "../RailwayLineLogo";
import { StationDropdown } from "../StationDropdown";
import { Box, Text } from "@/shared/ui/base";
import { Button } from "@/shared/ui/components";
import { calculateDistanceMeters, calculateWalkMinutes } from "../../api";
import type {
  Station,
  RailwayLine,
  SupportedLanguage,
  LocalizedName,
} from "../../types";

// =============================================================================
// Types
// =============================================================================

export interface SelectedStation {
  id: string;
  name: LocalizedName;
  walk_minutes: number;
  distance_m?: number;
  line_name?: LocalizedName;
}

/** Grouped station with all lines merged */
interface GroupedStation {
  /** Primary station ID (first occurrence) */
  id: string;
  /** Station name (nested structure) */
  name: LocalizedName;
  /** Distance in meters (closest) */
  distance_m?: number;
  /** All railway lines serving this station */
  lines: RailwayLine[];
  /** Original station objects */
  stations: Station[];
}

export interface NearestStationSelectorProps {
  /** Address for checking if empty (used for tooltip) */
  address?: string;
  /** City slug to limit station search */
  citySlug?: string;
  /** Currently selected stations */
  selectedStations: SelectedStation[];
  /** Callback when selection changes */
  onSelectionChange: (stations: SelectedStation[]) => void;
  /** Maximum number of stations that can be selected */
  maxStations?: number;
  /** Search radius in meters */
  radiusM?: number;
  /** Language for station names */
  lang: SupportedLanguage;
  /** Label for the field */
  label?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether address has been validated (enables station selection) */
  isAddressValidated: boolean;
  /** Validated latitude from parent (to avoid re-validation) */
  validatedLat?: number;
  /** Validated longitude from parent (to avoid re-validation) */
  validatedLon?: number;
}

// =============================================================================
// Component
// =============================================================================

export const NearestStationSelector = observer(
  (props: NearestStationSelectorProps) => {
    const {
      address,
      citySlug,
      selectedStations,
      onSelectionChange,
      maxStations = 5,
      radiusM = 1500,
      lang,
      label,
      disabled = false,
      isAddressValidated,
      validatedLat,
      validatedLon,
    } = props;

    const t = useTranslation();
    const { geoStore } = useStore();

    // State
    const [suggestedStations, setSuggestedStations] = useState<Station[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showManualSelect, setShowManualSelect] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Geocoded coordinates for distance calculation
    const [geocodedCoords, setGeocodedCoords] = useState<{
      lat: number;
      lon: number;
    } | null>(null);

    // Check if address is empty
    const isAddressEmpty = !address || address.trim().length === 0;

    // Manual trigger for auto-search - uses validated coordinates from parent
    const handleAutoSearch = useCallback(async () => {
      // Check if we have validated coordinates from parent
      if (!validatedLat || !validatedLon) {
        setError(
          t("listing.nearestStation.enterAddress") ||
            "Please validate your address first",
        );
        return;
      }

      setIsLoading(true);
      setError(null);
      setShowManualSelect(false);

      try {
        // Use validated coordinates directly - no need to re-validate
        setGeocodedCoords({ lat: validatedLat, lon: validatedLon });

        const stations = await geoStore.findNearbyStations(
          validatedLat,
          validatedLon,
          {
            radiusM,
            citySlug,
            limit: 20,
            lang,
          },
        );
        setSuggestedStations(stations);
        setHasSearched(true);
      } catch (err) {
        console.error("Failed to fetch nearby stations:", err);
        setError(
          t("listing.nearestStation.fetchError") ||
            "Failed to fetch nearby stations",
        );
      } finally {
        setIsLoading(false);
      }
    }, [validatedLat, validatedLon, citySlug, radiusM, lang, t, geoStore]);

    // DISTANCE_TOLERANCE_M for grouping stations
    const DISTANCE_TOLERANCE_M = 100;

    // Group stations by name (Japanese name for uniqueness)
    const groupedStations = useMemo(() => {
      const grouped = new Map<string, GroupedStation>();

      for (const station of suggestedStations) {
        const key = station.name.ja;

        if (grouped.has(key)) {
          const existing = grouped.get(key)!;
          if (
            station.distance_m &&
            (!existing.distance_m || station.distance_m < existing.distance_m)
          ) {
            existing.distance_m = station.distance_m;
            existing.id = station.id;
          }
          existing.stations.push(station);
        } else {
          grouped.set(key, {
            id: station.id,
            name: station.name,
            distance_m: station.distance_m,
            lines: [],
            stations: [station],
          });
        }
      }

      // Collect lines from stations within tolerance
      for (const [, groupedStation] of grouped) {
        const minDist = groupedStation.distance_m || 0;
        const maxDist = minDist + DISTANCE_TOLERANCE_M;

        for (const station of groupedStation.stations) {
          if (!station.distance_m || station.distance_m <= maxDist) {
            for (const line of station.lines) {
              const lineExists = groupedStation.lines.some(
                (l) =>
                  l.line_code === line.line_code ||
                  l.line_name.ja === line.line_name.ja,
              );
              if (!lineExists) {
                groupedStation.lines.push(line);
              }
            }
          }
        }
      }

      return Array.from(grouped.values())
        .sort((a, b) => (a.distance_m || Infinity) - (b.distance_m || Infinity))
        .slice(0, 10);
    }, [suggestedStations]);

    // Convert GroupedStation to SelectedStation
    const groupedToSelected = useCallback(
      (grouped: GroupedStation): SelectedStation => ({
        id: grouped.id,
        name: grouped.name,
        walk_minutes: grouped.distance_m
          ? calculateWalkMinutes(grouped.distance_m)
          : 5,
        distance_m: grouped.distance_m,
        line_name: grouped.lines[0]?.line_name,
      }),
      [],
    );

    // Check if station is selected
    const isSelected = useCallback(
      (nameJa: string) => selectedStations.some((s) => s.name.ja === nameJa),
      [selectedStations],
    );

    // Toggle grouped station selection
    const toggleGroupedStation = useCallback(
      (grouped: GroupedStation) => {
        if (disabled) return;

        const selected = isSelected(grouped.name.ja);

        if (selected) {
          onSelectionChange(
            selectedStations.filter((s) => s.name.ja !== grouped.name.ja),
          );
        } else if (selectedStations.length < maxStations) {
          onSelectionChange([...selectedStations, groupedToSelected(grouped)]);
        }
      },
      [
        disabled,
        isSelected,
        selectedStations,
        onSelectionChange,
        maxStations,
        groupedToSelected,
      ],
    );

    // Remove station
    const removeStation = useCallback(
      (nameJa: string) => {
        if (disabled) return;
        onSelectionChange(selectedStations.filter((s) => s.name.ja !== nameJa));
      },
      [disabled, selectedStations, onSelectionChange],
    );

    // Handle manual selection - calculate distance if we have geocoded coords
    const handleManualSelect = useCallback(
      (station: Station) => {
        if (disabled) return;

        if (
          !isSelected(station.name.ja) &&
          selectedStations.length < maxStations
        ) {
          // If we have geocoded coordinates and station has location, calculate distance
          let distance_m: number | undefined = station.distance_m;
          let walk_minutes = station.distance_m
            ? calculateWalkMinutes(station.distance_m)
            : 5;

          if (geocodedCoords && station.lat && station.lon) {
            distance_m = calculateDistanceMeters(
              geocodedCoords.lat,
              geocodedCoords.lon,
              station.lat,
              station.lon,
            );
            walk_minutes = calculateWalkMinutes(distance_m);
          }

          const selectedStation: SelectedStation = {
            id: station.id,
            name: station.name,
            walk_minutes,
            distance_m,
            line_name: station.lines[0]?.line_name,
          };

          onSelectionChange([...selectedStations, selectedStation]);
        }
      },
      [
        disabled,
        isSelected,
        selectedStations,
        onSelectionChange,
        maxStations,
        geocodedCoords,
      ],
    );

    // Format distance
    const formatDistance = (distanceM?: number) => {
      if (!distanceM) return "";
      if (distanceM < 1000) return `${Math.round(distanceM)}m`;
      return `${(distanceM / 1000).toFixed(1)}km`;
    };

    // Get display name based on language
    const getDisplayName = (item: { name: LocalizedName }) => {
      if (lang === "ja") return item.name.ja;
      if (lang === "en") return item.name.en;
      return item.name.ru || item.name.en;
    };

    return (
      <Box className="space-y-3">
        {/* Label */}
        {label && (
          <Text as="label" className="block text-sm font-medium text-gray-700">
            {label}
          </Text>
        )}

        {/* Action buttons - shown when not in manual mode and haven't searched yet */}
        {!showManualSelect && !hasSearched && (
          <Box className="flex flex-wrap gap-3">
            {/* AI-powered Find Nearby button with glow variant */}
            <Box className="relative group">
              <Button
                variant="glow"
                onClick={handleAutoSearch}
                disabled={disabled || !isAddressValidated}
                loading={isLoading}
                icon={<Sparkles className="w-4 h-4" />}
              >
                {t("listing.nearestStation.autoSearch") ||
                  "Find Nearby Stations"}
              </Button>
              {/* Tooltip when address is empty */}
              {isAddressEmpty && (
                <Box className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                  {t("listing.nearestStation.enterAddress") ||
                    "Enter address first"}
                </Box>
              )}
            </Box>

            {/* Manual Search button */}
            <Button
              variant="secondary"
              onClick={() => setShowManualSelect(true)}
              disabled={disabled}
            >
              <Search className="w-4 h-4 mr-2" />
              {t("listing.nearestStation.manualSelect") || "Manual Search"}
            </Button>
          </Box>
        )}

        {/* Error state */}
        {error && (
          <Text variant="body" className="text-sm text-red-500">
            {error}
          </Text>
        )}

        {/* Suggested stations chips - shown after auto search in glowing card */}
        {!isLoading &&
          hasSearched &&
          groupedStations.length > 0 &&
          !showManualSelect && (
            <Box className="card-ai-glow ai-glow-shadow p-4">
              <Box className="flex items-center justify-between mb-4">
                <Text variant="h6" className="text-gray-900">
                  {t("listing.nearestStation.suggested") ||
                    "Suggested Stations"}
                </Text>
                <button
                  type="button"
                  onClick={() => {
                    setHasSearched(false);
                    setSuggestedStations([]);
                    setGeocodedCoords(null);
                  }}
                  className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  aria-label={t("common.close") || "Close"}
                >
                  <X className="w-5 h-5" />
                </button>
              </Box>
              {/* Station chips */}
              <Box className="flex flex-wrap gap-2 mb-4">
                {groupedStations.map((grouped) => {
                  const selected = isSelected(grouped.name.ja);
                  const validLines = grouped.lines.filter(
                    (line) => line.line_code && line.color,
                  );

                  return (
                    <button
                      key={grouped.name.ja}
                      type="button"
                      onClick={() => toggleGroupedStation(grouped)}
                      disabled={
                        disabled ||
                        (!selected && selectedStations.length >= maxStations)
                      }
                      className={`
                    inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm
                    transition-all duration-200
                    ${
                      selected
                        ? "bg-green-100 text-green-800 ring-2 ring-green-400"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }
                    ${disabled || (!selected && selectedStations.length >= maxStations) ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                  `}
                    >
                      {validLines.length > 0 ? (
                        <Box className="flex items-center gap-0.5 -ml-0.5">
                          {validLines.slice(0, 3).map((line, index) => (
                            <RailwayLineLogo
                              key={line.line_code || index}
                              lineCode={line.line_code!}
                              color={line.color!}
                              size={18}
                            />
                          ))}
                          {validLines.length > 3 && (
                            <Text
                              as="span"
                              className="flex items-center justify-center text-[10px] font-semibold shadow-sm text-gray-600 bg-gray-300"
                              style={{
                                width: 18,
                                height: 18,
                                borderRadius: "50%",
                                marginLeft: 2,
                              }}
                            >
                              +{validLines.length - 3}
                            </Text>
                          )}
                        </Box>
                      ) : (
                        <Train className="w-4 h-4" />
                      )}
                      <Text as="span">{getDisplayName(grouped)}</Text>
                      {grouped.distance_m && (
                        <Text as="span" className="text-xs opacity-75">
                          ({formatDistance(grouped.distance_m)})
                        </Text>
                      )}
                    </button>
                  );
                })}
              </Box>

              {/* Show manual search link */}
              <button
                type="button"
                onClick={() => setShowManualSelect(true)}
                className="text-sm text-violet-600 hover:text-violet-700 hover:underline"
                disabled={disabled}
              >
                {t("listing.nearestStation.notYourStations") ||
                  "Can't find your station? Search manually"}
              </button>
            </Box>
          )}

        {/* No results after search */}
        {!isLoading &&
          hasSearched &&
          groupedStations.length === 0 &&
          !showManualSelect && (
            <Box className="space-y-3">
              <Text variant="body" className="text-sm text-gray-500">
                {t("listing.nearestStation.noResults") ||
                  "No stations found nearby"}
              </Text>
              <Box className="flex flex-wrap gap-2">
                {/* Try Again button - reset to initial state */}
                <Button
                  variant="primary"
                  onClick={() => {
                    setHasSearched(false);
                    setSuggestedStations([]);
                    setError(null);
                  }}
                  disabled={disabled}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t("listing.nearestStation.tryAgain") || "Try Again"}
                </Button>
                {/* Manual Search button */}
                <Button
                  variant="secondary"
                  onClick={() => setShowManualSelect(true)}
                  disabled={disabled}
                >
                  <Search className="w-4 h-4 mr-2" />
                  {t("listing.nearestStation.manualSelect") || "Manual Search"}
                </Button>
              </Box>
            </Box>
          )}

        {/* Manual search dropdown */}
        {showManualSelect && (
          <Box className="space-y-2">
            <Box className="flex items-center justify-between">
              <Text
                variant="body"
                className="text-sm font-medium text-gray-700"
              >
                {t("listing.nearestStation.manualSelect") || "Manual Search"}
              </Text>
              <button
                type="button"
                onClick={() => {
                  setShowManualSelect(false);
                  setGeocodedCoords(null); // Reset coords so we re-fetch on next search
                }}
                className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label={t("common.close") || "Close"}
              >
                <X className="w-5 h-5" />
              </button>
            </Box>
            <StationDropdown
              citySlug={citySlug}
              lang={lang}
              onSelect={handleManualSelect}
              disabled={disabled || selectedStations.length >= maxStations}
              selectedIds={selectedStations.map((s) => s.id)}
            />
          </Box>
        )}

        {/* Selected stations list - shown below manual search when in manual mode */}
        {selectedStations.length > 0 && (
          <Box className="space-y-2">
            <Text variant="body" className="text-sm font-medium text-gray-700">
              {t("listing.nearestStation.selected") || "Selected Stations"} (
              {selectedStations.length}/{maxStations})
            </Text>
            <Box className="flex flex-wrap gap-2">
              {selectedStations.map((station) => (
                <button
                  key={station.name.ja}
                  type="button"
                  onClick={() => !disabled && removeStation(station.name.ja)}
                  disabled={disabled}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-green-100 text-green-800 hover:bg-green-200 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Text as="span">{getDisplayName(station)}</Text>
                  <Text as="span" className="text-xs">
                    ({station.walk_minutes}{" "}
                    {t("listing.nearestStation.minWalk") || "min"})
                  </Text>
                  {!disabled && <X className="w-3.5 h-3.5 ml-0.5" />}
                </button>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  },
);
